/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { FullScreenSheet, useLocalization } from "@lemmonhq/uikit";
import type { StudioCampaign } from "./types";
import { createDraftCampaign, getStudioCampaign, saveStudioCampaign, STUB_SEGMENTS } from "./stub";
import { providePreview } from "./shared";
import { checkCampaign, worstLevel } from "./checks";
import { AudienceStep } from "./steps/AudienceStep";
import { OfferStep } from "./steps/OfferStep";
import { ContentStep } from "./steps/ContentStep";
import { ReviewStep } from "./steps/ReviewStep";

type CampaignBuilderProps = {
  campaignId: string | null;
  onClose: () => void;
};

const STEPS = [
  { key: "audience", icon: "group" },
  { key: "offer", icon: "confirmation_number" },
  { key: "content", icon: "edit_note" },
  { key: "review", icon: "rocket_launch" },
] as const;

export const CampaignBuilder = defineComponent<CampaignBuilderProps>({
  name: "CampaignBuilder",
  props: ["campaignId", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const existing = props.campaignId ? getStudioCampaign(props.campaignId) : null;
    // Edit a detached copy (the store is reactive, so clone via JSON); nothing
    // reaches the store until Save.
    const draft = ref<StudioCampaign>(existing ? JSON.parse(JSON.stringify(existing)) : createDraftCampaign());
    const step = ref(existing ? 2 : 0);
    const dirty = ref(false);
    const savedAt = ref<Date | null>(null);
    providePreview(draft);

    const patch = (p: Partial<StudioCampaign>) => {
      draft.value = { ...draft.value, ...p };
      dirty.value = true;
    };

    const checks = computed(() =>
      checkCampaign(draft.value, STUB_SEGMENTS.find((s) => s.id === draft.value.segment_id)),
    );
    const blocked = computed(() => worstLevel(checks.value) === "error");

    const save = (status?: StudioCampaign["status"]) => {
      draft.value = saveStudioCampaign({ ...draft.value, ...(status ? { status } : {}) });
      dirty.value = false;
      savedAt.value = new Date();
    };

    const launch = (close: () => void) => {
      save(draft.value.schedule.type === "now" ? "sending" : "scheduled");
      close();
    };

    const StepNav = () => (
      <nav class="flex shrink-0 items-center justify-center gap-1 overflow-x-auto border-b border-base-300 bg-base-100 px-4 py-2">
        {STEPS.map((s, i) => (
          <div key={s.key} class="flex items-center">
            <button
              type="button"
              class={[
                "flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition",
                step.value === i ? "bg-primary text-primary-content" : "text-base-content/70 hover:bg-base-200",
              ]}
              onClick={() => (step.value = i)}
            >
              <span
                class={[
                  "flex h-5 w-5 items-center justify-center rounded-full text-xs",
                  step.value === i ? "bg-primary-content/20" : "bg-base-300",
                ]}
              >
                {i + 1}
              </span>
              {t(`campaigns.studio.step_${s.key}`)}
            </button>
            {i < STEPS.length - 1 && <span class="mx-1 h-px w-6 bg-base-300" />}
          </div>
        ))}
      </nav>
    );

    const Body = () => {
      switch (step.value) {
        case 0:
          return <AudienceStep campaign={draft.value} onChange={patch} />;
        case 1:
          return <OfferStep campaign={draft.value} onChange={patch} />;
        case 2:
          return <ContentStep campaign={draft.value} onChange={patch} />;
        default:
          return <ReviewStep campaign={draft.value} onChange={patch} onGoTo={(s) => (step.value = s)} />;
      }
    };

    return () => (
      <FullScreenSheet
        isOpen={true}
        onClose={props.onClose}
        class="z-50"
        canClose={() => !dirty.value || window.confirm(t("campaigns.studio.discard_changes"))}
      >
        {{
          default: ({ close }: { close: () => void }) => (
            <div class="flex h-full flex-col bg-base-200 text-base-content">
              <header class="flex shrink-0 items-center gap-3 border-b border-base-300 bg-base-100 px-4 py-2">
                <button type="button" class="btn btn-ghost btn-sm btn-square" onClick={close}>
                  <span class="material-symbols-outlined">close</span>
                </button>
                <input
                  type="text"
                  class="input input-ghost input-sm min-w-0 flex-1 text-lg font-semibold"
                  placeholder={t("campaigns.studio.name_placeholder")}
                  value={draft.value.name}
                  onInput={(e) => patch({ name: (e.target as HTMLInputElement).value })}
                />
                <span class="hidden text-xs text-base-content/50 sm:inline">
                  {dirty.value
                    ? t("campaigns.studio.unsaved")
                    : savedAt.value
                      ? t("campaigns.studio.saved")
                      : ""}
                </span>
                <span class="badge badge-ghost">{t(`campaigns.studio.status_${draft.value.status}`)}</span>
                <button type="button" class="btn btn-ghost btn-sm" onClick={() => save()}>
                  {t("campaigns.studio.save_draft")}
                </button>
                {step.value === STEPS.length - 1 ? (
                  <button
                    type="button"
                    class="btn btn-primary btn-sm gap-1"
                    disabled={blocked.value}
                    title={blocked.value ? t("campaigns.studio.fix_errors_first") : undefined}
                    onClick={() => launch(close)}
                  >
                    <span class="material-symbols-outlined text-base">
                      {draft.value.schedule.type === "now" ? "send" : "schedule_send"}
                    </span>
                    {draft.value.schedule.type === "now" ? t("campaigns.studio.send_now") : t("campaigns.studio.schedule")}
                  </button>
                ) : (
                  <button type="button" class="btn btn-primary btn-sm gap-1" onClick={() => step.value++}>
                    {t("campaigns.studio.next")}
                    <span class="material-symbols-outlined text-base">arrow_forward</span>
                  </button>
                )}
              </header>
              {StepNav()}
              <main class={["min-h-0 flex-1", step.value === 2 ? "overflow-hidden" : "overflow-y-auto"]}>{Body()}</main>
            </div>
          ),
        }}
      </FullScreenSheet>
    );
  },
});
